import {
  createDecisionCenterContext,
} from "./decisionCenterContext";

import {
  decisionPrioritizationEngine,
  executiveDecisionReasoningEngine,
} from "../engines";

import {
  portfolioRecommendationAdapter,
  scenarioRecommendationAdapter,
  adaptRiskFinancingEngine,
  riskFinancingRecommendationAdapter,
  populateDecisionMatters,
} from "../adapters";

import { executePortfolioDecision } from "../../execution/executePortfolioDecision";
import { createDecisionOutcome } from "../models";


export function aggregateDecisionCenterData({
  decisionAnalytics = null,
  portfolioData = null,
  scenarioData = null,
  financingEngine = null,
  governanceData = null,
  valueRealizationData = null,
  portfolioDecisionExecutionInput,
} = {}) {

  const portfolioRecommendations =
    portfolioRecommendationAdapter(
      portfolioData
    );

  const scenarioRecommendations =
    scenarioRecommendationAdapter(
      scenarioData
    );

  const financingResult =
    adaptRiskFinancingEngine(
        financingEngine
    );

  const financingDecisions =
   riskFinancingRecommendationAdapter({
      portfolioData:
        financingResult.sourceData?.portfolio,

      decisionData:
        financingResult.sourceData?.decisionAnalysis,

      coverageData:
        financingResult.sourceData?.coverage,
    });

  const rawDecisions = [
    ...financingDecisions,
  ];


  const decisions =
    decisionPrioritizationEngine(
      rawDecisions
    );

  const recommendations = [
    ...portfolioRecommendations,
    ...scenarioRecommendations,
    ...financingDecisions,
  ].filter((recommendation, index, all) => recommendation && all.findIndex(
    (candidate) => candidate?.id === recommendation.id &&
      candidate?.engine === recommendation.engine
  ) === index);

  const decisionMatters =
    populateDecisionMatters(
      recommendations
    );

  const decisionOutcomes = decisions.map((decision) => {
    const matter = decisionMatters.find(
      (candidate) => candidate.createdFromRecommendationId === decision.id
    );

    return createDecisionOutcome({
      // Risk Financing `transferredLoss` describes exposure transferred by the
      // financing position. It is not evidence of incremental impact caused by
      // the Executive Capital Allocation, so it must not populate monitoring.
      expectedImpact: null,
      decisionMatterId: matter?.id ?? null,
      createdFromRecommendationId: decision.id ?? null,
      sourceRecommendation: matter?.sourceRecommendation ?? decision,
    });
  });

  const overallPosition =
    buildOverallPosition({
      decisionAnalytics,
      portfolioData,
      scenarioData,
      financingResult,
      decisions,
    });

  const executiveDecision = executiveDecisionReasoningEngine({
    portfolioEvidence: portfolioData,
    unconstrainedEconomicOptimization: portfolioData?.unconstrainedEconomicOptimization ?? null,
    constrainedEconomicOptimization: portfolioData?.constrainedEconomicOptimization ?? null,
    availableCapital: portfolioData?.availableCapital ?? portfolioData?.submittedAvailableCapital ?? portfolioData?.approvedAvailableCapital ?? null,
    currentPortfolioPosition: overallPosition,
    scenarioEvidence: scenarioData ? { ...scenarioData, recommendations: scenarioRecommendations } : null,
    riskFinancingEvidence: financingResult?.sourceData ? { ...financingResult.sourceData, recommendations: financingDecisions } : null,
    // Organization-wide governance is supporting evidence, not the approval
    // contract for this recommendation.
    governanceEvidence: null,
    provenance: portfolioData?.provenance ?? financingResult?.sourceData?.provenance ?? null,
  });

  const context = createDecisionCenterContext({
    analytics: decisionAnalytics,
    currency: decisionAnalytics?.positions?.gross_portfolio_exposure?.analyticsContext?.currency,
    valueRealizationContext: valueRealizationData,
    overallPosition,
    recommendations,
    decisions,
    decisionOutcomes,
    executiveDecision,

    engines: {
      portfolio: {
        status: portfolioEngineStatus(portfolioData),
        data: portfolioData,
        executiveRecommendation: portfolioRecommendations.find(
          ({ source }) => source === "capital_allocation"
        ) ?? null,
        execution: portfolioExecutionReference(portfolioData),
        diagnostics: portfolioDiagnostics(portfolioData),
      },

      scenario: {
        status: scenarioData
          ? "ready"
          : "idle",
        data: scenarioData,
      },

      financing: {
        status: financingResult.status,
        data:
            financingResult.sourceData,
      },

      governance: {
        status: governanceData?.status ?? "unavailable",
        reason: governanceData?.reason ?? null,
        data: governanceData?.data ?? null,
      },
    },
  });

  const governanceContext = {
    ...context,
    decisionMatters,
  };

  return portfolioDecisionExecutionInput === undefined
    ? governanceContext
    : {
        ...governanceContext,
        execution: executePortfolioDecision(portfolioDecisionExecutionInput),
      };
}

function portfolioOptimization(portfolioData) {
  return portfolioData?.combinedBundleOptimization ??
    portfolioData?.combined_bundle_optimization ?? null;
}

function portfolioDiagnostics(portfolioData) {
  const optimization = portfolioOptimization(portfolioData);
  const recommendation = optimization?.executiveRecommendation ??
    optimization?.executive_recommendation ?? null;
  const mode = portfolioData?.portfolioAnalysisOptimizationMode ??
    portfolioData?.portfolio_analysis_optimization_mode ?? "LEGACY";
  if (!portfolioData) return { status: "UNAVAILABLE", mode, recommendationAvailable: false };
  if (mode === "LEGACY" && !optimization) {
    return { status: "AVAILABLE", mode, recommendationAvailable: false };
  }
  let status = "AVAILABLE";
  if (!optimization) status = "UNAVAILABLE";
  else if (!optimization.policy && optimization.policyStatus === "UNAVAILABLE") {
    status = "POLICY_UNAVAILABLE";
  } else if (optimization.status === "EVIDENCE_UNAVAILABLE") {
    status = "INCOMPLETE_EVIDENCE";
  } else if (!recommendation) status = "UNAVAILABLE";
  else if (!recommendation.identity?.recommendationId ||
      !recommendation.headline || !recommendation.boardRationale) {
    status = "INVALID_CONTRACT";
  }
  return {
    status,
    mode,
    optimizerStatus: optimization?.status ?? null,
    recommendationAvailable: status === "AVAILABLE" && recommendation != null,
    policyStatus: optimization?.policyStatus ?? null,
    warnings: optimization?.warnings ?? [],
  };
}

function portfolioEngineStatus(portfolioData) {
  return portfolioDiagnostics(portfolioData).status;
}

function portfolioExecutionReference(portfolioData) {
  const optimization = portfolioOptimization(portfolioData);
  if (!optimization) return null;
  const recommendation = optimization.executiveRecommendation ??
    optimization.executive_recommendation ?? null;
  return {
    status: optimization.status ?? null,
    candidateComparisonReference: recommendation
      ?.supportingEvidence?.candidateComparisonReference ?? null,
    selectedBundleReference: recommendation
      ?.supportingEvidence?.selectedBundleReference ?? null,
  };
}

function buildOverallPosition({
  decisionAnalytics,
  portfolioData,
  scenarioData,
  financingResult,
  decisions,
}) {
  const financingPosition =
    financingResult?.overallPosition || {};

const normalizedDecisions =
  Array.isArray(decisions)
    ? decisions
    : [];


const decisionsRequiringAttention =
  normalizedDecisions.filter(
    (decision) => {

      const priorityLevel =
        String(
          decision?.priority?.level ||
          ""
        )
          .trim()
          .toUpperCase();


      return (
        priorityLevel ===
          "CRITICAL" ||
        priorityLevel ===
          "HIGH"
      );

    }
  ).length;

  const positions = decisionAnalytics?.positions ?? {};
  const metric = (name, key) => {
    const position = positions[name];
    return position?.availability === "AVAILABLE"
      ? resolveNumber(position.metrics?.[key])
      : null;
  };

  return {
    expectedAnnualLoss: metric("gross_portfolio_exposure", "eal"),
    capitalAtRiskP95: metric("capital_tail_position", "p95"),

    decisionsRequiringAttention,
    capitalRequired: null,
    residualRisk: null,
    grossPortfolioExposure: positions.gross_portfolio_exposure ?? null,
    capitalTailPosition: positions.capital_tail_position ?? null,
    postMitigationPortfolioExposure: positions.post_mitigation_portfolio_exposure ?? null,
    transferredFinancialExposure: positions.transferred_financial_exposure ?? null,
    retainedFinancialExposure: positions.retained_financial_exposure ?? null,
    aboveLimitUninsuredExposure: positions.above_limit_uninsured_exposure ?? null,


    coverageRatio:
      resolveNumber(
        financingPosition.coverageRatio,
        null
      ),

    scenariosWithProtectionGaps:
      resolveNumber(
        financingPosition
          .scenariosWithProtectionGaps
      ),

    riskAppetitePosition:
      resolveValue(
        financingPosition.riskAppetitePosition,
        portfolioData?.executive_recommendation
          ?.current_position?.risk_appetite_position,
        portfolioData?.summary?.risk_appetite_position,
        scenarioData?.riskAppetitePosition,
        null
      ),

    activeScenarios:
      resolveNumber(
        financingPosition.activeScenarios,
        null
      ),

    coveredScenarios:
      resolveNumber(
        financingPosition.coveredScenarios,
        null
      ),

    uncoveredScenarios:
      resolveNumber(
        financingPosition
          .uncoveredScenarios,

        null
      ),

    activeCoverages:
      resolveNumber(
        financingPosition.activeCoverages,
        null
      ),

    expiringCoverages:
      resolveNumber(
        financingPosition
          .expiringCoverages,

        null
      ),
  };
}


function resolveNumber(...values) {
  for (const value of values) {
    if (
      value !== null &&
      value !== undefined &&
      value !== "" &&
      Number.isFinite(Number(value))
    ) {
      return Number(value);
    }
  }

  return null;
}

function resolveValue(...values) {
  return values.find(
    (value) =>
      value !== null &&
      value !== undefined &&
      value !== ""
  ) ?? null;
}
